import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import Form from "../../components/StaticPageForm";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import {
  getAboutUs,
  upsertAboutUs,
} from "../../services/aboutUs/aboutUsServices";
import {
  AboutUs,
  LocalizedText,
  UpdateAboutUsDto,
} from "../../services/generated/client";
import styles from "./styles.module.css";
import { useApi } from "../../services/api";

const AboutUsPage = () => {
  const { withErrorHandling } = useApi();
  const [aboutUs, setAboutUs] = useState<UpdateAboutUsDto>({
    header1: localizedTextDefaultValue,
    header2: localizedTextDefaultValue,
    paragraph1: localizedTextDefaultValue,
    paragraph2: localizedTextDefaultValue,
    accomplishments: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        const response = await getAboutUs();
        if (response) {
          setAboutUs(response);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchAboutUs();
  }, []);

  const handleUpdateAboutUs = async (e: FormEvent) => {
    e.preventDefault();

    const { header1, header2, paragraph1, paragraph2, accomplishments } =
      aboutUs;

    const newAboutUs = await withErrorHandling(async () => {
      return await upsertAboutUs({
        header1,
        header2,
        paragraph1,
        paragraph2,
        accomplishments,
      });
    });
    if (newAboutUs) {
      setAboutUs(newAboutUs);
    }
  };

  const handleMultiLanguageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as keyof UpdateAboutUsDto;
    const value = e.target.value;

    setAboutUs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] as LocalizedText),
        [languageEditing]: value,
      },
    }));
  };

  if (isLoading) {
    return <div className={styles.mainContent}>Loading...</div>;
  }

  return (
    <div className={styles.mainContent}>
      <div className={styles.formConteiner}>
        <div className={styles.formWrapper}>
          <LanguageSwitcher
            languageEditing={languageEditing}
            setLanguageEditing={setLanguageEditing}
            inForm={true}
          />
          <Form
            title={"Edit AboutUs"}
            submitLabel={"Update"}
            languageEditing={languageEditing}
            onSubmit={handleUpdateAboutUs}
            isView={false}
            fields={[
              {
                id: "header1",
                label: `Header 1 ${languageEditing.toUpperCase()}`,
                type: "text",
                value:
                  aboutUs?.header1?.[languageEditing as keyof LocalizedText] ||
                  "",
                onChange: (e) =>
                  handleMultiLanguageInputChange(
                    e as ChangeEvent<HTMLInputElement>
                  ),
              },
              {
                id: "header2",
                label: `Header 2 ${languageEditing.toUpperCase()}`,
                type: "text",
                value:
                  aboutUs?.header2?.[languageEditing as keyof LocalizedText] ||
                  "",
                onChange: (e) =>
                  handleMultiLanguageInputChange(
                    e as ChangeEvent<HTMLInputElement>
                  ),
              },
              {
                id: "paragraph1",
                label: `Paragraph 1 ${languageEditing.toUpperCase()}`,
                type: "textarea",
                value:
                  aboutUs?.paragraph1?.[
                    languageEditing as keyof LocalizedText
                  ] || "",
                onChange: (e) =>
                  handleMultiLanguageInputChange(
                    e as ChangeEvent<HTMLInputElement>
                  ),
              },
              {
                id: "paragraph2",
                label: `Paragraph 2 ${languageEditing.toUpperCase()}`,
                type: "textarea",
                value:
                  aboutUs?.paragraph2?.[
                    languageEditing as keyof LocalizedText
                  ] || "",
                onChange: (e) =>
                  handleMultiLanguageInputChange(
                    e as ChangeEvent<HTMLInputElement>
                  ),
              },
              {
                id: "accomplishment",
                label: "Accomplishment Cards",
                type: "accomplishments",
                accomplishments: aboutUs?.accomplishments || [],
                onAccomplishmentChange: (index, key, value) =>
                  setAboutUs((prev) => {
                    const updated = [...(prev.accomplishments || [])];
                    updated[index] = { ...updated[index], [key]: value };
                    return { ...prev, accomplishments: updated };
                  }),
                onAddAccomplishment: () =>
                  setAboutUs((prev) => {
                    return {
                      ...prev,
                      accomplishments: [
                        ...(prev.accomplishments ?? []),
                        { number: "", header: "", paragraph: "" },
                      ],
                    } as AboutUs;
                  }),
                onRemoveAccomplishment: (index) =>
                  setAboutUs((prev) => {
                    const updated = [...(prev.accomplishments || [])];
                    updated.splice(index, 1);
                    return { ...prev, accomplishments: updated };
                  }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
